import type { TimerSettings, AppSettings } from "@/lib/types";
import { isTauri } from "@/utils/supabase/tauri-storage";
import {
  parseStoredSettings,
  serializeStoredSettings,
  resolveSettingSync,
  type StoredSettingEnvelope,
} from "@/lib/settings-sync";
import { getUserSettings, upsertUserSetting } from "@/lib/supabase-client";
import { DEFAULT_DISPLAY_ZOOM } from "@/lib/display-zoom";
import { DEFAULT_COLOR_THEME } from "@/lib/color-theme";
import { DEFAULT_TASK_DETAIL_PEEK_WIDTH } from "@/lib/task-detail-peek-width";

// デフォルトのタイマー設定
export const DEFAULT_TIMER_SETTINGS: TimerSettings = {
  workNotificationMinutes: 25,
  notificationEnabled: true,
  mode: "stopwatch",
  pomodoroWorkMinutes: 25,
  pomodoroShortBreakMinutes: 5,
  pomodoroLongBreakMinutes: 15,
  pomodoroLongBreakInterval: 4,
  pomodoroAutoStartBreak: true,
};

// 後方互換性のためのエイリアス
export const DEFAULT_POMODORO_SETTINGS = DEFAULT_TIMER_SETTINGS;

const SETTINGS_KEY = "timer-settings";
const OLD_SETTINGS_KEY = "pomodoro-settings";

// Tauri Store インスタンス管理
let tauriStore: any = null;
let tauriStorePromise: Promise<any> | null = null;

async function getTauriStore(): Promise<any> {
  if (tauriStore) return tauriStore;
  if (tauriStorePromise) return tauriStorePromise;

  tauriStorePromise = (async () => {
    const { load } = await import("@tauri-apps/plugin-store");
    tauriStore = await load("settings.json", { autoSave: true } as any);
    return tauriStore;
  })();

  return tauriStorePromise;
}

// ===== ローカル読み書き (Web: localStorage / Tauri: plugin-store) =====

async function readRaw(key: string): Promise<string | null> {
  if (isTauri()) {
    const store = await getTauriStore();
    return (await store.get<string>(key)) ?? null;
  }
  return localStorage.getItem(key);
}

async function writeRaw(key: string, raw: string): Promise<void> {
  if (isTauri()) {
    const store = await getTauriStore();
    await store.set(key, raw);
    await store.save();
  } else {
    localStorage.setItem(key, raw);
  }
}

// ===== 保存 (ローカル + DB push) =====

// ローカルは v2 封筒形式 { __v, value, updatedAt, dirty } で保存する。
// DB push に失敗 (オフライン等) した場合は dirty=true のまま残し、
// 次回の syncSettingsWithRemote() で再送する。
async function saveSetting<T>(key: string, value: T): Promise<void> {
  const updatedAt = Date.now();
  try {
    await writeRaw(
      key,
      serializeStoredSettings({ __v: 2, value, updatedAt, dirty: true })
    );
  } catch (error) {
    console.error("[Settings] Save error:", error);
    return;
  }
  try {
    const pushed = await upsertUserSetting(key, value, updatedAt);
    if (pushed) {
      await writeRaw(
        key,
        serializeStoredSettings({ __v: 2, value, updatedAt, dirty: false })
      );
    }
  } catch {
    // オフライン等。dirty のまま残して次回同期で再送
  }
}

async function loadSettingEnvelope<T>(
  key: string
): Promise<StoredSettingEnvelope<T> | null> {
  try {
    return parseStoredSettings<T>(await readRaw(key));
  } catch (error) {
    console.error("[Settings] Load error:", error);
    return null;
  }
}

/**
 * タイマー設定を保存
 */
export async function saveTimerSettings(
  settings: TimerSettings
): Promise<void> {
  await saveSetting(SETTINGS_KEY, settings);
}

// 後方互換性のためのエイリアス
export const savePomodoroSettings = saveTimerSettings;

/**
 * 旧設定から新設定へマイグレーション
 */
function migrateOldSettings(oldSettings: Record<string, unknown>): TimerSettings {
  return {
    ...DEFAULT_TIMER_SETTINGS,
    workNotificationMinutes: typeof oldSettings.workDuration === 'number'
      ? oldSettings.workDuration
      : DEFAULT_TIMER_SETTINGS.workNotificationMinutes,
    notificationEnabled: typeof oldSettings.notificationEnabled === 'boolean'
      ? oldSettings.notificationEnabled
      : DEFAULT_TIMER_SETTINGS.notificationEnabled,
  };
}

/**
 * タイマー設定を読み込み
 */
export async function loadTimerSettings(): Promise<TimerSettings> {
  const envelope = await loadSettingEnvelope<TimerSettings>(SETTINGS_KEY);
  if (envelope) {
    // デフォルト値とマージして欠けているプロパティを補完
    return { ...DEFAULT_TIMER_SETTINGS, ...envelope.value };
  }

  // 旧設定 (pomodoro-settings キー) からマイグレーション
  try {
    const oldEnvelope = await loadSettingEnvelope<Record<string, unknown>>(
      OLD_SETTINGS_KEY
    );
    if (oldEnvelope) {
      const migrated = migrateOldSettings(oldEnvelope.value);
      // 新しい形式で保存
      await saveTimerSettings(migrated);
      return migrated;
    }
  } catch (error) {
    console.error("[Settings] Load error:", error);
  }

  return DEFAULT_TIMER_SETTINGS;
}

// 後方互換性のためのエイリアス
export const loadPomodoroSettings = loadTimerSettings;

// ===== アプリ設定 =====

const APP_SETTINGS_KEY = "app-settings";

// デフォルトのアプリ設定
export const DEFAULT_APP_SETTINGS: AppSettings = {
  defaultScheduleToday: false,
  digestEnabled: false,
  digestTime: "09:00",
  taskDetailOpenMode: "peek",
  displayZoom: DEFAULT_DISPLAY_ZOOM,
  colorTheme: DEFAULT_COLOR_THEME,
  taskDetailPeekWidth: DEFAULT_TASK_DETAIL_PEEK_WIDTH,
  boardSortModes: {},
  todaySortMode: "manual",
  mobileDigestPush: true,
  achievements: [],
};

/**
 * アプリ設定を保存
 */
export async function saveAppSettings(settings: AppSettings): Promise<void> {
  await saveSetting(APP_SETTINGS_KEY, settings);
}

/**
 * アプリ設定を読み込み
 */
export async function loadAppSettings(): Promise<AppSettings> {
  const envelope = await loadSettingEnvelope<AppSettings>(APP_SETTINGS_KEY);
  if (envelope) {
    // デフォルト値とマージして欠けているプロパティを補完
    return { ...DEFAULT_APP_SETTINGS, ...envelope.value };
  }
  return DEFAULT_APP_SETTINGS;
}

// ===== DB 同期 =====

export interface SettingsSyncResult {
  /** リモートの方が新しく、ローカルに反映した場合のみ入る */
  timerSettings?: TimerSettings;
  appSettings?: AppSettings;
}

let cachedSyncResult: SettingsSyncResult | null = null;
let syncInFlight: Promise<SettingsSyncResult> | null = null;

/**
 * ローカル設定と DB (user_settings) を key 単位の last-write-wins で同期する。
 * - リモートの方が新しい key → ローカルへ反映し、結果に含めて返す (store が UI に反映する)
 * - ローカルが dirty / リモート未作成の key → DB へ push
 * - 未ログイン時は何もしない (次回呼び出しで再試行)
 * 認証済みで一度成功したら以降は結果をキャッシュして返す (アプリ起動ごとに1回)。
 */
export async function syncSettingsWithRemote(): Promise<SettingsSyncResult> {
  if (cachedSyncResult) return cachedSyncResult;
  if (syncInFlight) return syncInFlight;

  syncInFlight = (async () => {
    let remote: Record<string, { value: unknown; updatedAt: number }> | null;
    try {
      remote = await getUserSettings();
    } catch (error) {
      console.error("[Settings] Sync fetch error:", error);
      return {};
    }
    // 未ログイン: キャッシュせず、ログイン後の呼び出しで再試行させる
    if (remote === null) return {};

    const result: SettingsSyncResult = {};

    const timer = await syncKey<TimerSettings>(SETTINGS_KEY, remote[SETTINGS_KEY]);
    if (timer) {
      result.timerSettings = { ...DEFAULT_TIMER_SETTINGS, ...timer };
    }

    const app = await syncKey<AppSettings>(APP_SETTINGS_KEY, remote[APP_SETTINGS_KEY]);
    if (app) {
      result.appSettings = { ...DEFAULT_APP_SETTINGS, ...app };
    }

    cachedSyncResult = result;
    return result;
  })().finally(() => {
    syncInFlight = null;
  });

  return syncInFlight;
}

// 1つの key を同期。リモートを採用した場合はその値を返す (それ以外は null)
async function syncKey<T>(
  key: string,
  remoteEntry: { value: unknown; updatedAt: number } | undefined
): Promise<T | null> {
  const local = await loadSettingEnvelope<T>(key);
  const remote = remoteEntry
    ? { value: remoteEntry.value as T, updatedAt: remoteEntry.updatedAt }
    : null;

  const action = resolveSettingSync(local, remote);

  if (action === "adoptRemote" && remote) {
    try {
      await writeRaw(
        key,
        serializeStoredSettings({
          __v: 2,
          value: remote.value,
          updatedAt: remote.updatedAt,
          dirty: false,
        })
      );
    } catch (error) {
      console.error("[Settings] Sync save error:", error);
    }
    return remote.value;
  }

  if (action === "pushLocal" && local) {
    // 旧形式 (updatedAt=0) は push 時点のタイムスタンプを刻む
    const updatedAt = local.updatedAt || Date.now();
    try {
      const pushed = await upsertUserSetting(key, local.value, updatedAt);
      if (pushed) {
        await writeRaw(
          key,
          serializeStoredSettings({
            __v: 2,
            value: local.value,
            updatedAt,
            dirty: false,
          })
        );
      }
    } catch {
      // オフライン等。dirty のまま残して次回同期で再送
    }
  }

  return null;
}
